import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { getScriptInfo } from "../clients/grpc";
import {
  GetScriptInfoRequest,
  ScriptInfo,
  Character,
} from "../generated/v1alpha1/script_processing_pb";
import "./ScriptDetails.css";
import { AuthContext } from "../contexts/AuthContext";

const ScriptDetails: React.FC = () => {
  const { scriptName } = useParams<{ scriptName: string }>();
  const [scriptInfo, setScriptInfo] = useState<ScriptInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { accessToken } = useContext(AuthContext);

  useEffect(() => {
    const fetchScriptDetails = async () => {
      if (!scriptName) return;
      try {
        const request = new GetScriptInfoRequest();
        request.setScriptName(scriptName);
        const response = await getScriptInfo(request);
        setScriptInfo(response.getInfo() || null);
      } catch (err) {
        setError((err as Error).message);
      }
    };
    fetchScriptDetails();
  }, [scriptName, accessToken]);

  if (error) return <div>Error: {error}</div>;
  if (!scriptInfo) return <div>Loading...</div>;

  return (
    <div className="script-details-container">
      {/* Title */}
      <h1 className="text-4xl font-extrabold text-center mb-6">
        {scriptInfo.getScriptTitle()}
      </h1>

      {/* Script Info */}
      <div className="bg-white shadow rounded-2xl p-6 space-y-4 border border-gray-200">
        <p className="text-gray-700">{scriptInfo.getDescription()}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-gray-600">
          <div>
            <h3 className="text-2xl font-bold mb-4">Genre</h3>
            {scriptInfo.getGenre()}
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-4">
              {`Writer${scriptInfo.getAuthor().includes(",") ? "s" : ""}`}
            </h3>
            {scriptInfo.getAuthor()}
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-4">Number of pages</h3>
            {scriptInfo.getNumPages()}
          </div>
        </div>
      </div>

      {/* Key Moments */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Key Moments</h2>
        <ul className="space-y-3">
          {scriptInfo.getKeyMomentsList().map((moment: string, index: number) => (
            <li
              key={index}
              className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm"
            >
              {moment}
            </li>
          ))}
        </ul>
      </div>

      {/* Characters */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Characters</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {scriptInfo.getCharactersList().map(
            (character: Character, index: number) => (
              <div
                key={character.getName()}
                className="bg-white shadow rounded-2xl p-5 border border-gray-200"
              >
                <h3 className="text-lg font-semibold text-gray-800">
                  {character.getName()}
                  <span className="ml-2 text-sm text-gray-500">
                  </span>
                </h3>
                <p className="mt-2 text-gray-700">{character.getDescription()}</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ScriptDetails;
